import React, { useState, useEffect } from "react";

const PregnancyDueDateCalculator = () => {
  // Inputs
  const [lmpDate, setLmpDate] = useState("2025-01-01"); // default LMP for immediate calculation
  const [conceptionDate, setConceptionDate] = useState(""); // optional
  const [cycleLength, setCycleLength] = useState(28); // typical cycle length (days) - used if wanting small adjustment
  const [useCycleAdjustment, setUseCycleAdjustment] = useState(false);
  const [result, setResult] = useState({
    method: "LMP",
    estimatedDueDate: "",
    estimatedDueDateFromConception: "",
    gestationalWeeks: "0",
    gestationalDays: "0",
    totalGestationDays: 0,
    daysRemaining: 0,
    conceptionBasedDaysRemaining: 0,
    conceptionBasedGestWeeks: "0",
    conceptionBasedGestDays: "0",
    pregnancyStatus: "",
    notes: "",
    lmpDisplay: "",
    conceptionDisplay: "",
  });

  // Helpers
  const parseDate = (dateStr) => {
    // dateStr expected in yyyy-mm-dd format
    if (!dateStr) return null;
    const d = new Date(dateStr + "T00:00:00");
    return isNaN(d.getTime()) ? null : d;
  };

  const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDisplayDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  const diffInDays = (fromDate, toDate) => {
    const msPerDay = 24 * 60 * 60 * 1000;
    const f = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
    const t = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
    return Math.round((t - f) / msPerDay);
  };

  const toWeeksAndDays = (totalDays) => {
    if (totalDays < 0) return { weeks: 0, days: 0 };
    const weeks = Math.floor(totalDays / 7);
    const days = totalDays % 7;
    return { weeks, days };
  };

  // Calculation logic
  const calculateDueDates = () => {
    const today = new Date();
    const lmp = parseDate(lmpDate);
    const conception = parseDate(conceptionDate);

    // Defaults and validations
    if (!lmp && !conception) {
      setResult((prev) => ({
        ...prev,
        method: "None",
        estimatedDueDate: "",
        estimatedDueDateFromConception: "",
        gestationalWeeks: "0",
        gestationalDays: "0",
        totalGestationDays: 0,
        daysRemaining: 0,
        pregnancyStatus: "Please provide LMP or Conception date.",
        notes: "Enter at least one date to calculate the due date and gestational age.",
        lmpDisplay: "",
        conceptionDisplay: "",
      }));
      return;
    }

    // Standard obstetric rules:
    // - LMP method: Due Date = LMP + 280 days (40 weeks)
    // - Conception-based: Due Date = Conception Date + 266 days (38 weeks) (if conception known)
    // Optionally adjust LMP by average cycle length: If cycle length != 28 and useCycleAdjustment true,
    // Estimated Ovulation shifts by cycleLength - 28 days => Due date shifts by that many days.
    const LMP_TOTAL_DAYS = 280;
    const CONCEPTION_TOTAL_DAYS = 266;

    let adjustedLmp = lmp;
    if (lmp && useCycleAdjustment && cycleLength && cycleLength !== 28) {
      // If cycle is longer, ovulation occurs later => due date shifts forward by (cycleLength - 28)
      const shift = parseInt(cycleLength) - 28;
      adjustedLmp = addDays(lmp, shift);
    }

    const dueFromLMP = lmp ? addDays(adjustedLmp, LMP_TOTAL_DAYS) : null;
    const dueFromConception = conception ? addDays(conception, CONCEPTION_TOTAL_DAYS) : null;

    // Choose "method" used: prefer conception if provided
    const method = conception ? "Conception" : "LMP";

    // Gestational age calculation:
    // - Usually counted from LMP; if conception provided and user prefers, we will show both.
    // gestationalAgeDays = today - LMP (if LMP provided), otherwise estimate from conception +14 days
    let gestationDays = 0;
    if (lmp) {
      gestationDays = diffInDays(lmp, today);
    } else if (conception) {
      // If only conception provided, gestation is conception->today + 14 days (approx)
      gestationDays = diffInDays(conception, today) + 14;
    }

    // For conception based gestation display (weeks since conception)
    let conceptionGestationDays = 0;
    if (conception) {
      conceptionGestationDays = diffInDays(conception, today);
    }

    // Days remaining until due date (based on chosen method)
    let daysRemaining = 0;
    if (method === "Conception" && dueFromConception) {
      daysRemaining = diffInDays(today, dueFromConception);
    } else if (method === "LMP" && dueFromLMP) {
      daysRemaining = diffInDays(today, dueFromLMP);
    }

    // Determine pregnancy status messages (trimmed, safe)
    let pregnancyStatus = "";
    if (daysRemaining > 0) {
      pregnancyStatus = `Approximately ${daysRemaining} day(s) remaining until your estimated due date.`;
    } else if (daysRemaining === 0) {
      pregnancyStatus = "Estimated due date is today. Best wishes!";
    } else {
      pregnancyStatus = `Past estimated due date by ${Math.abs(daysRemaining)} day(s). Consider contacting your provider if still pregnant.`;
    }

    // Map gestation to weeks and days
    const gest = toWeeksAndDays(Math.max(0, gestationDays));
    const conceptionGest = toWeeksAndDays(Math.max(0, conceptionGestationDays));

    // Compose notes
    let notes = `Calculation method: ${method === "Conception" ? "Conception date (more precise if known)" : "Last Menstrual Period (standard obstetric method)"}.\n`;
    notes += "Due dates are estimates — actual delivery can occur +/- 2 weeks from estimated date.\n";
    notes += "If you have ultrasound measurements, those may provide a more accurate dating in early pregnancy.";

    setResult({
      method,
      estimatedDueDate: dueFromLMP ? formatDisplayDate(dueFromLMP) : "",
      estimatedDueDateFromConception: dueFromConception ? formatDisplayDate(dueFromConception) : "",
      gestationalWeeks: String(gest.weeks),
      gestationalDays: String(gest.days),
      totalGestationDays: gestationDays,
      daysRemaining,
      conceptionBasedDaysRemaining: conception ? diffInDays(today, dueFromConception) : null,
      conceptionBasedGestWeeks: String(conceptionGest.weeks),
      conceptionBasedGestDays: String(conceptionGest.days),
      pregnancyStatus,
      notes,
      lmpDisplay: lmp ? formatDisplayDate(lmp) : "",
      conceptionDisplay: conception ? formatDisplayDate(conception) : "",
    });
  };

  // Run default calculation on first load
  useEffect(() => {
  calculateDueDates();
}, [lmpDate, conceptionDate, cycleLength, useCycleAdjustment]);


  // UI handler wrappers keep existing structure consistent with your other calculators
  const handleLmpChange = (e) => {
    setLmpDate(e.target.value);
  };

  const handleConceptionChange = (e) => {
    setConceptionDate(e.target.value);
  };

  const handleCycleLengthChange = (val) => {
    setCycleLength(val);
  };

  const toggleCycleAdjustment = (val) => {
    setUseCycleAdjustment(val);
  };

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'white' }}>
              <path d="M12 2C9.79 2 8 3.79 8 6c0 2.21 1.79 4 4 4s4-1.79 4-4c0-2.21-1.79-4-4-4z"></path>
              <path d="M21 16.5A7.5 7.5 0 0 0 12 9h0a7.5 7.5 0 0 0-9 7.5V21h18z"></path>
            </svg>
          </div>
          <h1>Pregnancy Due Date Calculator</h1>
          <p>
            Estimate your expected due date and current gestational age using Last Menstrual Period (LMP) or Conception date. Provide both for best accuracy — the tool defaults to LMP when conception date is not provided.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                </svg>
                Pregnancy Dates
              </h2>

              {/* LMP Input */}
              <div className="input-group">
                <label>Last Menstrual Period (LMP)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📅</span>
                  <input
                    type="date"
                    value={lmpDate}
                    onChange={handleLmpChange}
                    className="calc-input"
                    placeholder="YYYY-MM-DD"
                  />
                </div>
                <div className="quick-select-group">
                  {/* quick select example recent dates */}
                  {["2025-01-01", "2025-02-01", "2025-03-01", "2025-04-01"].map((d) => (
                    <button
                      key={d}
                      onClick={() => setLmpDate(d)}
                      className="quick-select-btn primary"
                    >
                      {formatDisplayDate(new Date(d))}
                    </button>
                  ))}
                </div>
              </div>

              {/* Conception Date */}
              <div className="input-group">
                <label>Conception Date (Optional)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">👶</span>
                  <input
                    type="date"
                    value={conceptionDate}
                    onChange={handleConceptionChange}
                    className="calc-input"
                    placeholder="YYYY-MM-DD"
                  />
                  <span className="input-suffix" style={{ right: '0.75rem', pointerEvents: 'none' }}>%</span>
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  If you know the approximate conception/ovulation date (e.g., from fertility tracking or IVF), enter it for a more precise estimate.
                </p>
              </div>

              {/* Cycle Adjustment */}
              <div className="input-group">
                <label>Menstrual Cycle Length (days)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={cycleLength}
                    onChange={(e) => setCycleLength(Number(e.target.value))}
                    className="calc-input"
                    placeholder="28"
                    min="20"
                    max="40"
                  />
                  <span className="input-suffix">days</span>
                </div>
                <div className="quick-select-group">
                  {[26, 28, 30, 32].map((val) => (
                    <button
                      key={val}
                      onClick={() => handleCycleLengthChange(val)}
                      className="quick-select-btn secondary"
                    >
                      {val}d
                    </button>
                  ))}
                </div>
                <div style={{ marginTop: '0.5rem' }} className="quick-select-group">
                  {[
                    { value: true, label: "Adjust LMP by cycle length" },
                    { value: false, label: "Do not adjust" }
                  ].map((opt) => (
                    <button
                      key={String(opt.value)}
                      onClick={() => toggleCycleAdjustment(opt.value)}
                      className={`quick-select-btn ${useCycleAdjustment === opt.value ? 'primary' : 'secondary'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={calculateDueDates}
                className="calculate-btn"
              >
                Calculate Due Date
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 2"></path>
                </svg>
                Pregnancy Summary
              </h2>

              <div className="result-card">
                {/* Estimated Due Date (LMP) */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Estimated Due Date (LMP)</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'white' }}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 8v4l3 2"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.estimatedDueDate || "—"}
                  </div>
                  <div className="result-subtext">
                    Based on LMP + 280 days (40 weeks)
                  </div>
                </div>

                {/* Estimated Due Date (Conception) */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Estimated Due Date (Conception)</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#3b82f6' }}>
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.estimatedDueDateFromConception || "—"}
                  </div>
                  <div className="result-subtext">
                    Based on Conception + 266 days (38 weeks)
                  </div>
                </div>

                {/* Gestational Age (LMP) */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Gestational Age (from LMP)</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#10b981' }}>
                        <path d="M12 2v6l4 2"></path>
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.gestationalWeeks} wk {result.gestationalDays} d
                  </div>
                  <div className="result-subtext">
                    Since LMP {result.lmpDisplay || "—"}
                  </div>
                </div>

                {/* Gestation from conception */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Gestational Age (from Conception)</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#9333ea' }}>
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {conceptionDate ? `${result.conceptionBasedGestWeeks} wk ${result.conceptionBasedGestDays} d` : "—"}
                  </div>
                  <div className="result-subtext">
                    Since Conception {result.conceptionDisplay || "—"}
                  </div>
                </div>

                {/* Days remaining */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Days Remaining</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#3b82f6' }}>
                        <path d="M21 10h-6"></path>
                        <path d="M21 6h-6"></path>
                        <path d="M3 6h6"></path>
                        <path d="M3 10h6"></path>
                        <path d="M3 14h6"></path>
                        <path d="M21 14h-6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {typeof result.daysRemaining === "number" ? `${result.daysRemaining} day(s)` : "—"}
                  </div>
                  <div className="result-subtext">
                    {result.pregnancyStatus}
                  </div>
                </div>

                {/* Notes / Guidance */}
                <div className="breakdown-section" style={{ marginTop: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e6eef6' }}>
                  <h3 className="breakdown-title">Notes & Guidance</h3>
                  <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.4' }}>
                    {result.notes}
                  </p>
                </div>

                {/* Visual Breakdown - placeholder bars to match structure */}
                <div className="breakdown-section" style={{ marginTop: '1rem' }}>
                  <h3 className="breakdown-title">Pregnancy Progress</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Progress (approx)</span>
                      <span className="breakdown-percentage">
                        {result.totalGestationDays > 0 ? `${Math.min(100, Math.round((result.totalGestationDays / 280) * 100))}%` : "0%"}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${result.totalGestationDays > 0 ? Math.min(100, (result.totalGestationDays / 280) * 100) : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How the Due Date is Calculated</h3>
              <p>
                This calculator provides an estimated due date using:
                <ul>
                  <li><strong>LMP method:</strong> Adds 280 days (40 weeks) to the first day of the last menstrual period. This is the standard obstetric method and widely used by clinicians.</li>
                  <li><strong>Conception-based method:</strong> Adds 266 days (38 weeks) to the conception/ovulation date — used when conception date is known (e.g., IVF or tracked ovulation).</li>
                </ul>
                Both are estimates — actual delivery can occur earlier or later. Ultrasound dating in early pregnancy is the most accurate method for gestational age.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Formulas Used:</h4>
            <div className="formula-display">
              Due Date (LMP) = LMP + 280 days (40 weeks)
            </div>
            <div className="formula-display" style={{ marginTop: '0.5rem' }}>
              Due Date (Conception) = Conception Date + 266 days (38 weeks)
            </div>

            <div className="formula-params" style={{ marginTop: '0.75rem' }}>
              <div className="formula-param">
                <div className="formula-param-key">Gestational Age</div>
                <div className="formula-param-value">Today - LMP (in days) converted to weeks + days</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Days Remaining</div>
                <div className="formula-param-value">Due Date - Today (in days)</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>Which method is more accurate — LMP or conception?</h5>
              <p>
                Conception-based dating can be more accurate when the exact conception date is known (IVF cycle or tracked ovulation). However, early ultrasound (first trimester) is commonly used for the most accurate dating.
              </p>
            </div>

            <div className="faq-item">
              <h5>Why might my due date change after an ultrasound?</h5>
              <p>
                Ultrasound measures fetal size. If an early ultrasound estimate differs from LMP dating, clinicians may update the due date because ultrasound dating is more reliable, especially when LMP is uncertain.
              </p>
            </div>

            <div className="faq-item">
              <h5>What does "full-term" mean?</h5>
              <p>
                Full-term generally refers to 39-40 weeks. Early-term is 37-38 weeks, and post-term is beyond 42 weeks. Most deliveries occur between 37 and 42 weeks.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I rely on the calculator for medical decisions?</h5>
              <p>
                This tool provides estimates for planning and awareness. It is not a substitute for professional medical advice. Always consult your healthcare provider for clinical decisions and personalized care.
              </p>
            </div>

            <div className="faq-item">
              <h5>What if my cycle length is not 28 days?</h5>
              <p>
                If your cycle is consistently shorter or longer than 28 days, ovulation (and thus conception) shifts. Enabling cycle adjustment shifts the LMP-based due date accordingly, but ultrasound remains the most reliable method.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can the calculator show trimester ranges?</h5>
              <p>
                This version intentionally omits a visual trimester breakdown. If you want trimester labels or a timeline in the results, I can add that while keeping the same layout.
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PregnancyDueDateCalculator;
